import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetProductsListDocument,
  CreateProductDocument,
  type GetProductsListQuery,
  type ProductType,
  type CreateProductMutation,
  type CreateProductMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { useNotification } from '@/app/contexts/NotificationContext';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import { productTypeLabel } from '@/utils/productTypeLabel';

const PRODUCT_TYPES: ProductType[] = ['SERVICE', 'LICENSE', 'PHYSICAL', 'DIGITAL'];

interface Row extends Record<string, unknown> {
  id: string;
  name: string;
  number: string;
  type: string;
  sku: string | null;
  shortDescription: string | null;
  sort: number;
  createdAt: string;
  updatedAt: string;
}

export default function Products() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (iso: string) => new Date(iso).toLocaleString(locale);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [type, setType] = useState<ProductType>('SERVICE');
  const [sort, setSort] = useState('0');
  const [sku, setSku] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [longDescription, setLongDescription] = useState('');

  const { data, loading, refetch } = useQuery<GetProductsListQuery>(GetProductsListDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
  });

  const [createProduct, { loading: creating }] = useMutation<
    CreateProductMutation,
    CreateProductMutationVariables
  >(CreateProductDocument);

  const nodes = data?.products?.nodes ?? [];
  const rows: Row[] = nodes.map((n) => ({
    id: n.id,
    name: n.name,
    number: n.number,
    type: n.type,
    sku: n.sku ?? null,
    shortDescription: n.shortDescription ?? null,
    sort: n.sort,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
    { field: 'number', headerName: t('masterData.productNumber'), width: 120 },
    {
      field: 'sku',
      headerName: t('masterData.sku'),
      width: 110,
      valueFormatter: (v: string | null) => v || '–',
    },
    {
      field: 'type',
      headerName: t('common.type'),
      width: 150,
      valueFormatter: (v: string) => productTypeLabel(t, v),
    },
    {
      field: 'shortDescription',
      headerName: t('masterData.shortDescription'),
      flex: 1,
      minWidth: 140,
      valueFormatter: (v: string | null) => (v && v.length > 80 ? `${v.slice(0, 80)}…` : v || '–'),
    },
    { field: 'sort', headerName: t('masterData.sort'), width: 80 },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 150,
      valueFormatter: (v: string) => formatDate(v),
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDate(v),
    },
  ];

  const handleCreate = async () => {
    const s = parseInt(sort, 10);
    if (Number.isNaN(s)) {
      showError(t('errors.generic'));
      return;
    }
    try {
      const res = await createProduct({
        variables: {
          input: {
            name,
            number,
            type,
            sort: s,
            sku: sku.trim() || '',
            shortDescription: shortDescription.trim() || null,
            longDescription: longDescription.trim() || null,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createProduct?.product) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('masterData.created'));
      setOpen(false);
      setName('');
      setNumber('');
      setSku('');
      setShortDescription('');
      setLongDescription('');
      setSort('0');
      setType('SERVICE');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('masterData.products')}</Typography>
        {isSuperAdmin(user) && (
          <Button variant="contained" onClick={() => setOpen(true)}>
            {t('masterData.createProduct')}
          </Button>
        )}
      </Box>

      <DataTable<Row>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) => navigate(`/products/${row.id}`)}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('masterData.createProduct')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.productNumber')}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label={t('masterData.sku')}
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label={t('common.type')}
            value={type}
            onChange={(e) => setType(e.target.value as ProductType)}
            fullWidth
          >
            {PRODUCT_TYPES.map((x) => (
              <MenuItem key={x} value={x}>
                {productTypeLabel(t, x)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('masterData.sort')}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            type="number"
            fullWidth
          />
          <TextField
            label={t('masterData.shortDescription')}
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label={t('masterData.longDescription')}
            value={longDescription}
            onChange={(e) => setLongDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !name.trim() || !number.trim()}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
